import React, { Fragment } from 'react';
import { CustomTable, TableColumn } from '@/components/Table';
import { Button, InputText } from '@/components';
import { toCurrency } from '@/helpers/masks/toCurrency';
import { EventProductMainReport } from '@/model/EventProductMainReport';
import { EventProductDetailReportV2 } from '@/model/EventProductDetailReportV2';
import SuperCollapse from '@/components/sharedComponents/SuperCollapse';
import { ReportDropdownHeader } from '../../ReportDropdownHeader';

interface StateProps {
  main: EventProductMainReport;
  productName: string;
  userName: string;
  onChangeProductName: (value: string) => void;
  onChangeUserName: (value: string) => void;
  onFilter: () => void;
  onClearFilter: () => void;
}

export const SaleProductProductsContent: React.FC<StateProps> = ({
  main,
  productName,
  userName,
  onChangeProductName,
  onChangeUserName,
  onFilter,
  onClearFilter,
}) => {
  const defaultColumns: TableColumn<any>[] = [
    {
      name: 'Produto',
      selector: row => row.productName,
    },
    {
      name: 'Vendidos',
      selector: row => row.productSold,
    },
    {
      name: 'Valor',
      selector: row => row.productValue,
    },
    {
      name: 'Venda',
      selector: row => row.totalSaleVale,
    },
    {
      name: 'Comissão',
      selector: row => row.totalCommission,
    },
    {
      name: 'Venda + comissão',
      selector: row => row.totalSaleValueWithCommission,
    },
    {
      name: 'Estoque inicial',
      selector: row => row.initialInventory,
    },
    {
      name: 'Estoque disponível',
      selector: row => row.availableStock,
    },
  ];

  const withoutStockColumns: TableColumn<any>[] = [
    {
      name: 'Produto',
      selector: row => row.productName,
    },
    {
      name: 'Vendidos',
      selector: row => row.productSold,
    },
    {
      name: 'Valor',
      selector: row => row.productValue,
    },
    {
      name: 'Venda',
      selector: row => row.totalSaleVale,
    },
    {
      name: 'Comissão',
      selector: row => row.totalCommission,
    },
    {
      name: 'Venda + comissão',
      selector: row => row.totalSaleValueWithCommission,
    },
  ];

  const getColumns = (products: EventProductDetailReportV2[]): TableColumn<any>[] => {
    let showStock = false;
    // eslint-disable-next-line no-plusplus
    for (let p = 0; p < products.length; p++) {
      if (products[p].initialInventory > 0 || products[p].availableStock > 0) {
        showStock = true;
        break;
      }
    }
    if (showStock) {
      return defaultColumns;
    }
    return withoutStockColumns;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      onFilter();
    }
  };

  return (
    <Fragment>
      {/* Container de Filtros com Flexbox e Botões alinhados à direita na linha abaixo */}
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: '20px 24px',
          marginBottom: '24px',
          border: '1px solid #e9ecef',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.03)',
        }}
      >
        <h6
          style={{
            fontSize: '14px',
            fontWeight: 600,
            color: '#495057',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '16px',
          }}
        >
          Filtros de Pesquisa
        </h6>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ flex: '1 1 250px' }}>
              <InputText
                name="filterProductName"
                label="Filtrar por Produto"
                placeholder="Digite o nome do produto"
                value={productName}
                onChange={e => onChangeProductName(e.target.value)}
                keyDow={handleKeyDown}
              />
            </div>
            <div style={{ flex: '1 1 250px' }}>
              <InputText
                name="filterUserName"
                label="Filtrar por Usuário (Cortesia)"
                placeholder="Digite o nome do usuário"
                value={userName}
                onChange={e => onChangeUserName(e.target.value)}
                keyDow={handleKeyDown}
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <Button title="Pesquisar" theme="blue" onClick={onFilter} />
            <Button title="Limpar" theme="red" onClick={onClearFilter} />
          </div>
        </div>
      </div>

      <div className="mb-4">
        {main && main.products && main.products.length > 0 && (
          <div className="mb-3">
            <SuperCollapse
              title={
                <ReportDropdownHeader
                  title="Vendas Gerais por Produto"
                  customItemsCount={main.products.reduce(
                    (acc, curr) => acc + (curr.productsSold || 0),
                    0,
                  )}
                  customTotalValue={main.products.reduce(
                    (acc, curr) => acc + (curr.totalSaleVale || 0),
                    0,
                  )}
                  customCommissionValue={main.products.reduce(
                    (acc, curr) => acc + (curr.totalCommission || 0),
                    0,
                  )}
                />
              }
              content={
                <div className="p-3 bg-white rounded">
                  <CustomTable
                    theme="secondaryWithoutBorder"
                    numberRowsPerPage={0}
                    progressPending={false}
                    columns={getColumns(main.products)}
                    data={main.products.map(data => ({
                      productName: data.productName,
                      productSold: data.productsSold,
                      initialInventory: data.initialInventory,
                      availableStock: data.availableStock,
                      productValue: toCurrency(data.productValue),
                      totalSaleVale: toCurrency(data.totalSaleVale),
                      totalCommission: toCurrency(data.totalCommission),
                      totalSaleValueWithCommission: toCurrency(data.totalSaleValueWithCommission),
                    }))}
                  />
                </div>
              }
            />
          </div>
        )}

        {main && main.courtesies && main.courtesies.length > 0 && (
          <Fragment>
            {main.courtesies.map(userCourtesy => (
              <div
                key={userCourtesy.userId || userCourtesy.userName || 'unidentified'}
                className="mb-3"
              >
                <SuperCollapse
                  title={
                    <ReportDropdownHeader
                      title={`Cortesia - ${userCourtesy.userName || 'Usuário Não Identificado'}`}
                      customCourtesiesCount={userCourtesy.totalProductsSold}
                    />
                  }
                  content={
                    <div className="p-3 bg-white rounded">
                      <CustomTable
                        theme="secondaryWithoutBorder"
                        numberRowsPerPage={0}
                        progressPending={false}
                        columns={[
                          {
                            name: 'Produto',
                            selector: row => row.productName,
                          },
                          {
                            name: 'Quantidade Emitida',
                            selector: row => row.productsSold,
                          },
                        ]}
                        data={userCourtesy.products || []}
                      />
                    </div>
                  }
                />
              </div>
            ))}
          </Fragment>
        )}
      </div>
    </Fragment>
  );
};
